import { Box, Stack, Typography, Chip } from '@mui/material';
import DateTimeForTableCell from '../../../core-components/theme/components/DateTime/DateTimeForTableCell';
import TableActions from '../../../components/TableActions';
import OptionsMenuIcon from '../../../core-components/assets/icons/OptionMenu';
import { DataLogger } from '../hooks/useDataLoggerTable';
import { useEnableDisableDataLogger } from '../hooks/useEnableDisableDataLogger';

const isRowDisabled = (row: DataLogger) => row._id === "6836bdcd95e5886f804b5667";

const ActionCell = ({ row, onEditClick }: { row: DataLogger, onEditClick?: (id: string) => void }) => {
  const { mutate: enableDisableDataLogger } = useEnableDisableDataLogger();

  const handleDelete = () => {
    if (row?._id) {
      enableDisableDataLogger(row._id);
    }
  };

  return (
    <TableActions
      rowData={row}
      deletePermission="DataLogger_Configuration"
      editAction={() => onEditClick?.(row?._id)}
      deleteAction={handleDelete}
      disabled={isRowDisabled(row)}
    />
  );
};

export const DataLoggerTableColumns = (onEditClick?: (id: string) => void) => {
  const getRowStyle = (row: DataLogger) => ({
    opacity: row.isDeleted || isRowDisabled(row) ? 0.5 : 1,
    color: row.isDeleted || isRowDisabled(row) ? '#666' : 'inherit',
    pointerEvents: isRowDisabled(row) ? 'none' : 'auto',
  });

  const renderAsset = (asset: any) => {
    if (!asset) return '—';
    
    if (Array.isArray(asset)) {
      return (
        <Stack direction="row" gap={1} flexWrap="wrap">
          {asset.map((item, index) => (
            <Chip
              key={index}
              label={item.plantName || '—'}
              variant="outlined"
              size="small"
              sx={{
                backgroundColor: 'transparent',
                borderColor: '#000',
                color: '#000',
                '& .MuiChip-label': {
                  color: '#000',
                },
              }}
            />
          ))}
        </Stack>
      );
    } else {
      return (
        <Typography variant="body2">
          {asset.plantName || '—'}
        </Typography>
      );
    }
  };

  return [
    {
      field: 'name',
      headerName: 'Data Logger Name',
      flex: 1,
      minWidth: 200,
      renderCell: (_: any, row: DataLogger) => (
        <Typography 
          variant="body2" 
          sx={{ 
            cursor: 'pointer',
            ...getRowStyle(row)
          }}
        >
          {row.name ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'asset',
      headerName: 'Asset',
      flex: 1,
      minWidth: 200,
      renderCell: (_: any, row: DataLogger) => (
        <Box sx={getRowStyle(row)}>
          {renderAsset(row.assetId)}
        </Box>
      ),
    },
    {
      field: 'lastValueTime',
      headerName: 'Last Value Time',
      flex: 1,
      minWidth: 200,
      renderCell: (_: any, row: DataLogger) => (
        <Box sx={getRowStyle(row)}>
          {row.lastValueTime ? (
            <DateTimeForTableCell date={row.lastValueTime} dateStringFormat="" />
          ) : (
            <Typography variant="body2">—</Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'serialNumber',
      headerName: 'Serial Number',
      flex: 1,
      minWidth: 180,
      renderCell: (_: any, row: DataLogger) => (
        <Typography 
          variant="body2"
          sx={getRowStyle(row)}
        >
          {row.serialNumber ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'action',
      headerName: (
        <Box sx={{ display: 'flex', alignItems: 'end', justifyContent: 'end' }}>
          <OptionsMenuIcon />
        </Box>
      ),
      align: 'right',
      renderCell: (_: any, row: DataLogger) => (
        <ActionCell row={row} onEditClick={onEditClick} />
      ),
      width: 80,
    },
  ];
}; 